import { useEffect, useState } from "react";
import { collection, doc, onSnapshot, query, where, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { S, C } from "../ui";
import { TRADES } from "../constants";
import ScrollHint from "../core/ScrollHint";

// Open decision #3 (resolved 2026-09-07): Facility Admin owns templates.
// A facility-specific override (facilityId set) takes precedence over the
// global default (facilityId: null, seeded once via scripts/seed-templates.mjs)
// for the same trade — see FieldFlow.jsx for where that lookup happens.
export default function TemplatesPanel({ facilityId }) {
  const [selectedTrade, setSelectedTrade] = useState(TRADES[0]);
  const [globalTemplate, setGlobalTemplate] = useState(null);
  const [override, setOverride] = useState(null);
  const [itemsText, setItemsText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => onSnapshot(doc(db, "checklistTemplates", `global-${selectedTrade.toLowerCase()}`), s =>
    setGlobalTemplate(s.exists() ? s.data() : null)), [selectedTrade]);

  useEffect(() => onSnapshot(query(collection(db, "checklistTemplates"), where("facilityId", "==", facilityId)), s => {
    const docs = s.docs.map(d => ({ id: d.id, ...d.data() }));
    setOverride(docs.find(t => t.trade === selectedTrade) || null);
  }), [facilityId, selectedTrade]);

  useEffect(() => {
    const items = override ? override.items : globalTemplate ? globalTemplate.items : [];
    setItemsText(items.join("\n"));
  }, [override, globalTemplate]);

  const save = async () => {
    setSaving(true);
    const items = itemsText.split("\n").map(s => s.trim()).filter(Boolean);
    const id = override ? override.id : `${facilityId}-${selectedTrade.toLowerCase()}`;
    await setDoc(doc(db, "checklistTemplates", id), {
      facilityId, trade: selectedTrade, items, createdAt: serverTimestamp(),
    }, { merge: true });
    setSaving(false);
  };

  return (
    <div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>
        Each trade has a global default checklist. Save your own version
        here to override it just for this facility — vendors and internal
        staff working this trade will use whichever one exists.
      </div>

      <ScrollHint className="trade-scroll" outerStyle={{ marginBottom: 16 }} fadeColor={C.bg}>
        {TRADES.map(t => (
          <button key={t} onClick={() => setSelectedTrade(t)}
            style={{ ...S.pill(selectedTrade === t ? C.ink : C.white, selectedTrade === t ? C.white : C.muted), border: `1px solid ${C.border}`, cursor: "pointer", whiteSpace: "nowrap" }}>
            {t}
          </button>
        ))}
      </ScrollHint>

      <div style={{ background: C.white, borderRadius: 0, border: `1px solid ${C.border}`, padding: 20, maxWidth: 520 }}>
        <div style={{ ...S.kicker, marginBottom: 8 }}>
          {selectedTrade} checklist {override ? "(facility override)" : globalTemplate ? "(global default)" : "(none yet)"}
        </div>
        <textarea
          style={{ ...S.input, minHeight: 160, fontFamily: "monospace", fontSize: 13, resize: "vertical" }}
          value={itemsText}
          onChange={e => setItemsText(e.target.value)}
          placeholder="One checklist item per line"
        />
        <button style={S.btn()} disabled={saving} onClick={save}>{saving ? "Saving…" : "Save Facility Override"}</button>
      </div>
    </div>
  );
}
